import { useMutation, useQueryClient } from '@tanstack/react-query'
import { fetcher } from '@quickmd-llc/frontend-utils'
import { getGetIntakeformsQueryKey } from '@/api/generated/intake-form-template/intake-form-template'
import type { IntakeFormTemplateAdminApiOutContract } from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'
import { mapIntakeFormError } from './mapIntakeFormError'

/**
 * Creates a new intake form. Replaces the `createNewIntakeForm` thunk +
 * CREATE_NEW_INTAKE_FORM* reducer / IntakeFormListener flow.
 *
 * The old flow: CREATE -> setLoading(true); CREATE_..._SUCCESS -> setLoading(false) +
 * setIsIntakeFormWasCreated(true) (which the create page watched to navigate back to
 * Management and the table watched to refetch); CREATE_..._ERROR -> setError(mappedMessage).
 * Here `isPending` drives the spinner, `onSuccess` invalidates the collection query (so the
 * table refetches) and runs the caller's navigation, and `onError` surfaces the mapped
 * message via the caller's `onError`.
 *
 * The generated `postIntakeforms` does not accept a request body (the OpenAPI spec is missing
 * the requestBody schema, so Orval emitted a no-body mutation). We therefore send the body
 * through the shared `fetcher`, reusing the generated collection URL (the last element of the
 * collection query key) so it stays in sync on regeneration.
 */

export interface IntakeFormRequestBody {
  Name: string
  Questions: unknown[]
}

const getIntakeFormsUrl = (): string => {
  const key = getGetIntakeformsQueryKey()
  const url = key[key.length - 1]
  return typeof url === 'string' ? url : ''
}

export const useCreateIntakeForm = () => {
  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: (body: IntakeFormRequestBody) =>
      fetcher<IntakeFormTemplateAdminApiOutContract>({
        url: getIntakeFormsUrl(),
        method: 'POST',
        data: body,
      }),
  })

  const createIntakeForm = (
    body: IntakeFormRequestBody,
    opts?: { onSuccess?: () => void; onError?: (message: string) => void },
  ) => {
    mutate(body, {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetIntakeformsQueryKey() })
        opts?.onSuccess?.()
      },
      onError: (error) => {
        opts?.onError?.(mapIntakeFormError(error))
      },
    })
  }

  return { createIntakeForm, isPending }
}
